import React, { useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSortBy, useTable } from 'react-table';
import { Box, Flex, Table, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import { analyticsIds } from '../../constants/analyticsIds';
import { getInfiniteListData, getInfiniteListRenderedDataLength } from '../../services/util';
import PulseLoader from '../PulseLoader';
import SortIcon from '../SortIcon';

const ReactQueryInfiniteTable = ({
  tableColumns,
  reactQueryResponse,
  noDataMessage = 'Nothing to see here.',
  height = 'auto',
  maxHeight = '300px',
  rowKey = 'id',
  selectable = {
    selectedItemId: null,
    handleItemSelect: null
  },
  columnProps = {}
}) => {
  const { data: rQData } = reactQueryResponse;
  const columns = useMemo(() => tableColumns, [tableColumns]);

  const data = useMemo(() => getInfiniteListData(rQData), [rQData]);

  const dataLength = useMemo(() => getInfiniteListRenderedDataLength(reactQueryResponse), [rQData]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
    ...columnProps
  }, useSortBy);

  if (dataLength === 0) {
    return (
      <Box height={height} maxHeight={maxHeight}>
        <Text color="greyScale.700" textStyle="bodyLg500">{noDataMessage}</Text>
      </Box>
    );
  }

  return (
    <Box className="InfiniteScrollTableWrapper" height={height}>
      <InfiniteScroll
        dataLength={dataLength}
        hasMore={reactQueryResponse.hasNextPage}
        height={height}
        loader={<PulseLoader />}
        maxHeight={maxHeight}
        next={reactQueryResponse.fetchNextPage}
        scrollableTarget="scrollableDiv"
        scrollThreshold={0.75}
      >
        <Table position="relative" variant="simple" {...getTableProps()} className="InfiniteScrollTable">
          <Thead>
            {headerGroups.map(headerGroup => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map(column => {
                  const headerProps = {
                    ...column.getSortByToggleProps(),
                    colSpan: column.colSpan || 2
                  };
                  return (
                    <Th
                      {...column.getHeaderProps(headerProps)}
                      bg="greyScale.100"
                      color="greyScale.900"
                      position="sticky"
                      textAlign="center"
                      textStyle="bodySm600"
                      textTransform="none"
                      top={0}
                      zIndex={1}
                    >
                      <Flex alignItems="center" justifyContent={column.justifyContent || 'center'} whiteSpace="nowrap">
                        {column.render('Header')}
                        <SortIcon column={column} />
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>

          <Tbody {...getTableBodyProps()}>
            {rows.map(row => {
              prepareRow(row);

              if (selectable.handleItemSelect) {
                const { selectedItemId, handleItemSelect } = selectable;
                const rowSelected = parseInt(selectedItemId, 10) === parseInt(row.original[rowKey], 10);
                return (
                  <Tr
                    {...row.getRowProps()}
                    _hover={rowSelected
                      ? { bg: 'primaryScale.50' }
                      : { bg: 'primaryScale.100', cursor: 'pointer' }}
                    bg={rowSelected ? 'primaryScale.50' : 'white'}
                    data-analytics-id={analyticsIds.insuredProfilePage.recordsComponent.selectRecordRow}
                    key={row.original[rowKey]}
                    onClick={e => handleItemSelect(e, row.original[rowKey])}
                  >
                    {
                      row.cells.map(cell => (
                        <Td
                          {...cell.getCellProps()}
                          colSpan={cell.column.colSpan || 2}
                          p={2}
                          textAlign="center"
                        >
                          {cell.render('Cell')}
                        </Td>
                      ))
                    }
                  </Tr>
                );
              }

              return (
                <Tr
                  {...row.getRowProps()}
                  key={row.original[rowKey]}
                >
                  {
                    row.cells.map(cell => (
                      <Td
                        {...cell.getCellProps()}
                        colSpan={cell.column.colSpan || 2}
                        p={2}
                        textAlign="center"
                      >
                        {cell.render('Cell')}
                      </Td>
                    ))
                  }
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </InfiniteScroll>
    </Box>
  );
};

ReactQueryInfiniteTable.propTypes = {
  tableColumns: PropTypes.arrayOf(PropTypes.shape({})),
  reactQueryResponse: PropTypes.arrayOf(PropTypes.shape({})),
  rowKey: PropTypes.string,
  height: PropTypes.string,
  maxHeight: PropTypes.string,
  selectable: PropTypes.shape({
    selectedItemId: PropTypes.number,
    handleItemSelect: PropTypes.func
  }),
  noDataMessage: PropTypes.string,
  columnProps: PropTypes.shape({})
};

export default ReactQueryInfiniteTable;
